import React from "react";

/* =============================================================================
   Pick-up scheduler — replaces the react-datepicker text field.

   Two steps the customer can see at a glance: pick a day, pick a time. No
   calendar popup, no typing, no format to get wrong.

   ⚠ IT DECIDES NOTHING ABOUT TRADING HOURS. Which days are offered comes from
   isOpenDay(), and which times from filteredTimes() — both passed in from
   CartPage, both unchanged. This component only asks them and draws the answer.
   If a store's hours look wrong here, the bug is in those two functions, not
   in this file.

   ⚠ 30-minute slots, matching react-datepicker's default timeIntervals, which
   is what the old field used. Do not make this finer without checking the POS
   accepts the extra times — offering 15-minute slots would hand the store
   pick-up times it has never been sent before.

   ⚠ onChange receives a real Date, exactly as DatePicker's did, so
   handleCalendarChange in App.js is untouched.

   One behaviour is new: times already past are not offered. The old field
   would happily let someone select 9am when it was 2pm, as long as the store
   opened at 9.

   ⚠ TIME-OF-DAY SPLIT (8 Oct, Jono). A store trading 10am-8:30pm in 30-minute
   slots is 22 pills in one wall of text. So the slots are grouped into Morning,
   Afternoon and Evening (see PERIODS) and only the chosen group is listed.

   ⚠ The row shows ONLY the groups that have slots, and only when TWO OR MORE
   do. A day that trades afternoon-only lists its times directly, because a
   single "Afternoon" pill is a click that tells the customer nothing. (Real
   examples from the dev brand: Carindale trades 10am-8:30pm on a Tuesday —
   three groups — and 1pm-1:30pm tomorrow — no row at all.)

   ⚠ Choosing a period NEVER changes the booked time. It only filters the list.
   Auto-selecting the first slot of a period would silently move someone's
   pick-up time while they were still looking.
   ============================================================================= */

const SLOT_MINUTES = 30;
const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/* The time-of-day groups, in time order — the order IS the fallback order when
   nothing else decides which one opens.

   Half-open on the hour: fromHour inclusive, toHour exclusive. So noon lands in
   Afternoon (12:00 reads as "12pm", and nobody reading "Morning" expects midday)
   and 6pm lands in Evening, per Jono, 8 Oct.

   ⚠ Change a boundary HERE and nowhere else. Nothing downstream hard-codes 12
   or 18, and the row only ever shows the groups that actually have slots. */
const PERIODS = [
    { key: 'am',  label: 'Morning',   fromHour: 0,  toHour: 12 },
    { key: 'pm',  label: 'Afternoon', fromHour: 12, toHour: 18 },
    { key: 'eve', label: 'Evening',   fromHour: 18, toHour: 24 }
];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/* Local, rather than date-fns. This project does not declare date-fns in
   package.json - it is only present transitively under react-datepicker, so
   importing it directly builds today and breaks silently the day that package
   stops hoisting it. Two calls were not worth that. Mirrors date-fns addDays:
   returns a new Date and leaves the argument alone. */
function addDays(date, amount) {
    const d = new Date(date.getTime());
    d.setDate(d.getDate() + amount);
    return d;
}

function sameDay(a, b) {
    return !!a && !!b
        && a.getFullYear() === b.getFullYear()
        && a.getMonth() === b.getMonth()
        && a.getDate() === b.getDate();
}

function formatTime(d) {
    let h = d.getHours();
    const m = d.getMinutes();
    const suffix = h >= 12 ? 'pm' : 'am';
    h = h % 12;
    if (h === 0) { h = 12; }
    return `${h}${m === 0 ? '' : ':' + String(m).padStart(2, '0')}${suffix}`;
}

export default function PickupScheduler(props) {

    const committed = props.value instanceof Date ? props.value : null;

    // The day being browsed. Starts on the committed day so reopening the cart
    // shows the customer's own choice, not the first available day.
    const [browseDay, setBrowseDay] = React.useState(committed);

    /* Which half of the day is being browsed: 'am', 'pm', or null for "work it
       out". Null is the normal state and the resolver below decides — this only
       holds an explicit click.

       ⚠ Reset to null whenever the DAY changes, which is why setBrowseDay is
       always paired with it. Carrying 'pm' onto a morning-only day would show
       an empty list under a pill the day does not even have. */
    const [browsePeriod, setBrowsePeriod] = React.useState(null);

    function pickDay(d) {
        setBrowseDay(d);
        setBrowsePeriod(null);
    }

    // Keep the browsed day in step if the value changes from outside.
    React.useEffect(() => {
        if (committed && !sameDay(committed, browseDay)) {
            setBrowseDay(committed);
            setBrowsePeriod(null);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.value]);

    const now = new Date();

    // ---- Days on offer -----------------------------------------------------
    const days = [];
    for (let i = props.minDays; i <= props.maxDays; i++) {
        const d = addDays(now, i);
        d.setHours(0, 0, 0, 0);
        if (props.isOpenDay(d)) { days.push(d); }
    }

    const activeDay = browseDay && days.some((d) => sameDay(d, browseDay))
        ? browseDay
        : (days.length > 0 ? days[0] : null);

    // ---- Times on offer for that day --------------------------------------
    const slots = [];
    if (activeDay) {
        for (let mins = 0; mins < 24 * 60; mins += SLOT_MINUTES) {
            const slot = new Date(activeDay);
            slot.setHours(Math.floor(mins / 60), mins % 60, 0, 0);
            if (slot <= now) { continue; }          // never offer a time already gone
            if (props.filteredTimes(slot, activeDay)) { slots.push(slot); }
        }
    }

    /* ---- Morning / Afternoon / Evening ------------------------------------
       Boundaries are on PERIODS, above, so there is one definition of where
       each one starts and nothing here hard-codes 12 or 18.

       Only the periods that actually have slots are offered, and the row is
       hidden entirely unless TWO OR MORE do — a single "Evening" pill is a
       click that tells the customer nothing. */
    const periodsWithSlots = PERIODS
        .map((p) => ({
            ...p,
            list: slots.filter((s) => s.getHours() >= p.fromHour && s.getHours() < p.toHour)
        }))
        .filter((p) => p.list.length > 0);

    const splitPeriods = periodsWithSlots.length > 1;

    function periodKeyFor(date) {
        const h = date.getHours();
        const found = PERIODS.find((p) => h >= p.fromHour && h < p.toHour);
        return found ? found.key : null;
    }

    /* Precedence, and the order is the whole behaviour:
         1. an explicit click, when that period actually has slots today
         2. the period containing the already-booked time, if it is on this day,
            so reopening the cart lands on the customer's own choice
         3. the period holding the earliest slot
       An explicit click has to beat the booked time, or clicking "Evening"
       while holding a 10am booking would snap straight back to Morning. */
    let activePeriodKey = periodsWithSlots.length > 0 ? periodsWithSlots[0].key : null;
    if (splitPeriods) {
        const has = (k) => periodsWithSlots.some((p) => p.key === k);
        const committedKey = committed && sameDay(committed, activeDay)
            ? periodKeyFor(committed) : null;

        if (browsePeriod && has(browsePeriod)) {
            activePeriodKey = browsePeriod;
        } else if (committedKey && has(committedKey)) {
            activePeriodKey = committedKey;
        }
        // else: already the earliest, because PERIODS is in time order and
        // periodsWithSlots preserves it.
    }

    const activePeriod = periodsWithSlots.find((p) => p.key === activePeriodKey);
    const visibleSlots = splitPeriods && activePeriod ? activePeriod.list : slots;

    function rangeLabel(list) {
        if (list.length === 0) { return ''; }
        if (list.length === 1) { return formatTime(list[0]); }
        return `${formatTime(list[0])} – ${formatTime(list[list.length - 1])}`;
    }

    function dayLabel(d) {
        if (sameDay(d, now)) { return 'Today'; }
        if (sameDay(d, addDays(now, 1))) { return 'Tomorrow'; }
        return DAY_NAMES[d.getDay()];
    }

    if (days.length === 0) {
        return (
            <div className="wo-when">
                <span className="wo-when__label">Pick up time</span>
                <p className="wo-sched__none">
                    This store has no pick up times available at the moment.
                </p>
            </div>
        );
    }

    return (
        <div className="wo-when">
            <span className="wo-when__label">
                When would you like it?
                {committed && <em className="wo-sched__chosen">
                    {dayLabel(committed)} at {formatTime(committed)}
                </em>}
            </span>

            <div className="wo-sched">
                <div className="wo-sched__days" role="tablist" aria-label="Pick up day">
                    {days.map((d) => {
                        const on = sameDay(d, activeDay);
                        return (
                            <button
                                key={d.toISOString()}
                                type="button"
                                role="tab"
                                aria-selected={on}
                                className={`wo-sched__day${on ? ' wo-sched__day--on' : ''}`}
                                onClick={() => pickDay(d)}
                                disabled={props.disabled}
                            >
                                <span className="wo-sched__dayName">{dayLabel(d)}</span>
                                <span className="wo-sched__dayDate">{d.getDate()} {MONTHS[d.getMonth()]}</span>
                            </button>
                        );
                    })}
                </div>

                {/* Only the periods that have slots, and only when 2+ do — see the header. */}
                {splitPeriods &&
                    <div className="wo-sched__periods" role="tablist" aria-label="Time of day">
                        {periodsWithSlots.map((p) => {
                            const on = activePeriodKey === p.key;
                            return (
                                <button
                                    key={p.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={on}
                                    className={`wo-sched__period${on ? ' wo-sched__period--on' : ''}`}
                                    onClick={() => setBrowsePeriod(p.key)}
                                    disabled={props.disabled}
                                >
                                    <span className="wo-sched__periodName">{p.label}</span>
                                    {/* The range saves a click just to find out what is inside. */}
                                    <span className="wo-sched__periodRange">{rangeLabel(p.list)}</span>
                                </button>
                            );
                        })}
                    </div>}

                {visibleSlots.length === 0
                    ? <p className="wo-sched__none">
                        No times left on this day. Try another.
                      </p>
                    : <div className="wo-sched__slots">
                        {visibleSlots.map((s) => {
                            const on = committed && s.getTime() === committed.getTime();
                            return (
                                <button
                                    key={s.toISOString()}
                                    type="button"
                                    className={`wo-sched__slot${on ? ' wo-sched__slot--on' : ''}`}
                                    onClick={() => props.onChange(s)}
                                    disabled={props.disabled}
                                >
                                    {formatTime(s)}
                                </button>
                            );
                        })}
                      </div>}
            </div>

            {props.errorMessage &&
                <p className="wo-sched__err">{props.errorMessage}</p>}
        </div>
    );
}
